import React, { useState, createContext, useEffect, useRef } from 'react'; // Added useRef
import './index.css';
import './App.css';
import './custom.css';
import './assets/icons/icons.css';
import HomePage from './pages/HomePage';
import FriendsPage from './pages/FriendsPage';
import WalletPage from './pages/WalletPage';
import { UserDataProvider } from './contexts/UserDataContext';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';

// Import Appwrite services
import { 
  initializeAppData // Corrected name
} from './services/appwrite';

// Create contexts for modal state and Telegram user that can be accessed across components
export const ModalContext = createContext();
export const TelegramUserContext = createContext();

// Add page caching to prevent remounting on tab switches
const PageContainer = React.memo(({ isActive, children }) => (
  <div style={{ 
    display: isActive ? 'block' : 'none',
    width: '100%',
    height: '100%'
  }}>
    {children}
  </div>
));

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [isDailyLoginOpen, setIsDailyLoginOpen] = useState(false);
  const [isAppInitialized, setIsAppInitialized] = useState(false); // New state to track overall app initialization
  const [initialAppData, setInitialAppData] = useState(null); // To store data from initializeApp
  const hasInitializedAppRef = useRef(false); // Ref to ensure initialization runs once

  // State for telegramUser that can be used by context, updated after ref is set
  const [telegramUser, setTelegramUser] = useState({
    id: null,
    username: 'User',
    firstName: '',
    lastName: '',
    photoUrl: '',
    isVerified: false
  });

  // Use the Telegram WebApp hook for fullscreen and other features
  const { 
    isFullscreen, 
    viewportHeight
  } = useTelegramWebApp();

  // Initialize user and application data
  useEffect(() => {
    if (hasInitializedAppRef.current) {
      return;
    }

    const initializeUserAndLoadData = async (tgUser) => {
      if (!tgUser || !tgUser.id) {
        console.error("Telegram user data is invalid for initialization.");
        return;
      }
      
      try {
        // Extract referral code
        let referralCode = null;
        if (window.Telegram && window.Telegram.WebApp) {
          const tgWebApp = window.Telegram.WebApp;
          const startParam = tgWebApp.initDataUnsafe?.start_parameter;
          if (startParam) referralCode = startParam;
          if (!referralCode && tgWebApp.initData) {
            try {
              const initDataParams = new URLSearchParams(tgWebApp.initData);
              const startappParam = initDataParams.get('start_parameter') || initDataParams.get('startapp');
              if (startappParam) referralCode = startappParam;
              if (!referralCode) {
                const startParamMatch = tgWebApp.initData.match(/start_parameter=([^&]*)/);
                if (startParamMatch && startParamMatch[1]) referralCode = decodeURIComponent(startParamMatch[1]);
              }
            } catch (error) { /* Silently handle parsing errors */ }
          }
        }
        if (!referralCode) {
          try {
            const urlParams = new URLSearchParams(window.location.search);
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const urlRef = urlParams.get('ref') || urlParams.get('startapp') || urlParams.get('start_parameter') || urlParams.get('tgWebAppStartParam') ||
                           hashParams.get('ref') || hashParams.get('startapp') || hashParams.get('start_parameter') || hashParams.get('tgWebAppStartParam');
            if (urlRef) referralCode = urlRef;
          } catch (error) { /* Silently handle parsing errors */ }
        }
        if (!referralCode && window.tgWebAppStartParam) {
          referralCode = window.tgWebAppStartParam;
        }
        
        console.log(`[App.js] Calling initializeAppData for user: ${tgUser.id}, referral: ${referralCode}`);
        const appDataResult = await initializeAppData(tgUser, referralCode); // Call the service with correct name
        
        if (appDataResult && appDataResult.success) {
          console.log('[App.js] initializeAppData successful:', appDataResult);
          setInitialAppData(appDataResult); // Store all data from initializeAppData response
          setIsAppInitialized(true);
        } else {
          console.error('[App.js] Failed to initialize app data:', appDataResult?.error || 'Unknown error from initializeAppData');
          setIsAppInitialized(false);
        }
      } catch (error) {
        console.error('[App.js] Critical error during app initialization process:', error);
        setIsAppInitialized(false);
      }
    };
    
    let currentTelegramUser;
    
    if (window.Telegram && window.Telegram.WebApp) {
      const tgWebApp = window.Telegram.WebApp;
      tgWebApp.ready();
      try { tgWebApp.expand(); } catch (e) { /* silent */ }
      try {
        const version = tgWebApp.version || '6.0';
        const versionNum = parseFloat(version);
        if (versionNum >= 6.1) {
          if (tgWebApp.setHeaderColor) tgWebApp.setHeaderColor('#ffffff');
          if (tgWebApp.setBackgroundColor) tgWebApp.setBackgroundColor('#ffffff');
        }
        if (versionNum >= 7.0 && tgWebApp.disableVerticalSwipes) tgWebApp.disableVerticalSwipes();
        else if (versionNum >= 6.2 && tgWebApp.setVerticalSwipeStatus) tgWebApp.setVerticalSwipeStatus(false);
        if (tgWebApp.MainButton?.hide) tgWebApp.MainButton.hide();
        if (parseFloat(tgWebApp.version || '6.0') > 6.0 && tgWebApp.BackButton?.hide) tgWebApp.BackButton.hide();
      } catch (e) { /* silent */ }
      
      const user = tgWebApp.initDataUnsafe?.user || {};
      currentTelegramUser = {
        id: user.id || null,
        username: user.username || 'User',
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        photoUrl: user.photo_url || '',
        isVerified: !!user.is_verified
      };
    } else {
      currentTelegramUser = {
        id: '99999',
        username: 'test_user_dev_env',
        firstName: 'TestDevEnv',
        lastName: 'UserDevEnv',
        photoUrl: '',
        isVerified: false
      };
      console.log("[App.js] Running in dev mode (no Telegram WebApp), using mock user:", currentTelegramUser);
    }
    
    setTelegramUser(currentTelegramUser);       // Set state for context
    
    if (currentTelegramUser && currentTelegramUser.id) {
      hasInitializedAppRef.current = true; // Mark as attempted/in-progress
      initializeUserAndLoadData(currentTelegramUser);
    } else {
      console.error("[App.js] Telegram user ID is missing or invalid, cannot initialize app.");
      setIsAppInitialized(false); // Ensure app is not marked as initialized
    }
  }, []); // Empty dependency array ensures this effect runs once on mount

  return (
    <TonConnectUIProvider manifestUrl={`${window.location.origin}/tonconnect-manifest.json`}>
      <TelegramUserContext.Provider value={telegramUser}>
        <UserDataProvider initialData={initialAppData}>
          <ModalContext.Provider value={{ isDailyLoginOpen, setIsDailyLoginOpen }}>
            <div className={`App app-container telegram-mini-app ${
              isDailyLoginOpen ? 'modal-open' : ''
            } ${
              isFullscreen ? 'fullscreen' : ''
            } ${
              viewportHeight > 0 ? 'has-viewport-height' : ''
            }`}>
                <PageContainer isActive={currentPage === 'home'}>
                  <HomePage onNavigate={setCurrentPage} isAppInitialized={isAppInitialized} />
                </PageContainer>
                
                <PageContainer isActive={currentPage === 'friends'}>
                  <FriendsPage onNavigate={setCurrentPage} isActive={currentPage === 'friends'} />
                </PageContainer>
                
                <PageContainer isActive={currentPage === 'wallet'}>
                  <WalletPage onNavigate={setCurrentPage} />
                </PageContainer>
            </div>
          </ModalContext.Provider>
        </UserDataProvider>
      </TelegramUserContext.Provider>
    </TonConnectUIProvider>
  );
}

export default App;
